import type { IVersion } from "@/types";
import {
  IconBrandGoogle,
  IconFileTypePdf,
  IconFileTypePpt,
  IconPhoto,
  type Icon as TablerIcon,
} from "@tabler/icons-react";

// Matches the headless-service compression tiers (QUALITY_TIER_PRESETS).
export type TPdfQuality = "compressed" | "standard" | "print-ready";

// Output file of an export (headless pdfOptions.outputFormat). "pptx"
// renders the same multi-slide PDF on uniform 16:9 pages and converts it
// server-side; "images" rasterizes every PDF page into a zip of images.
export type TExportFormat = "pdf" | "pptx" | "images" | "gslides";

// What headless actually produces. "gslides" is a PPTX render that the editor
// then uploads to the user's Google Drive, where Drive converts it to a
// native Google Slides file.
export type TExportOutputFormat = "pdf" | "pptx" | "images";
export const EXPORT_OUTPUT_FORMAT: Record<TExportFormat, TExportOutputFormat> =
  {
    pdf: "pdf",
    pptx: "pptx",
    images: "images",
    gslides: "pptx",
  };

// Share dialog Export tab order.
export const EXPORT_FORMAT_IDS: TExportFormat[] = [
  "pdf",
  "pptx",
  "gslides",
  "images",
];

// Deck formats render one 16:9 page per slide (layout forced to multi-slide).
export const isDeckFormat = (format: TExportFormat): boolean =>
  EXPORT_OUTPUT_FORMAT[format] === "pptx";

// Formats a reader can download from a published version — each enabled and
// configured on its own, in version.data and as account defaults. Mirrors
// dotell-backend exports/reader-formats.js. Google Slides is an editor-only
// export (uploaded to the author's Drive), never a reader download.
export type TReaderDownloadFormat = Exclude<TExportFormat, "gslides">;

export const READER_DOWNLOAD_FORMATS = {
  pdf: {
    flag: "downloadablePdf",
    options: "pdfOptions",
    accountOptions: "pdfExportOptions",
  },
  pptx: {
    flag: "downloadablePptx",
    options: "pptxOptions",
    accountOptions: "pptxExportOptions",
  },
  images: {
    flag: "downloadableImages",
    options: "imagesOptions",
    accountOptions: "imagesExportOptions",
  },
} as const satisfies Record<
  TReaderDownloadFormat,
  { flag: string; options: string; accountOptions: string }
>;

export const READER_DOWNLOAD_FORMAT_IDS = Object.keys(
  READER_DOWNLOAD_FORMATS,
) as TReaderDownloadFormat[];

type TReaderDownloadKeys =
  (typeof READER_DOWNLOAD_FORMATS)[TReaderDownloadFormat];

/** Every format's flag + options on a version (downloadablePptx, pptxOptions, …). */
export type TReaderDownloadFields<TOptions> = {
  [K in TReaderDownloadKeys["flag"]]?: boolean;
} & { [K in TReaderDownloadKeys["options"]]?: TOptions };

/** Every format's flag + default options on the account. */
export type TAccountReaderDownloadFields<TOptions> = {
  [K in TReaderDownloadKeys["flag"]]?: boolean;
} & { [K in TReaderDownloadKeys["accountOptions"]]?: TOptions };

export const EXPORT_FORMAT_ICON: Record<TExportFormat, TablerIcon> = {
  pdf: IconFileTypePdf,
  pptx: IconFileTypePpt,
  gslides: IconBrandGoogle,
  images: IconPhoto,
};

export const EXPORT_FORMAT_LABEL: Record<TExportFormat, string> = {
  pdf: "PDF",
  pptx: "PowerPoint",
  images: "Images",
  gslides: "Google Slides",
};

// Extension of the downloaded file.
export const EXPORT_FORMAT_EXTENSION: Record<TExportFormat, string> = {
  pdf: "pdf",
  pptx: "pptx",
  images: "zip",
  gslides: "pptx",
};

export type TExportPdfData = {
  version: IVersion;
  layout: "multi-slide" | "one-page" | "a4-letter";
  slides: string[] | null;
  removeQRCode: boolean;
  highquality: boolean;
  // Optional so legacy callers (dashboard ShareDialog) keep the pre-tier
  // backend behavior when omitted.
  quality?: TPdfQuality;
  minPageRatio: boolean;
  // Defaults to "pdf" (legacy dashboard ShareDialog never sets it).
  format?: TExportFormat;
};

export type TPdfObj = {
  url: string;
  // JPEG cover screenshot from the same render as the PDF (may be absent —
  // the screenshot is best-effort).
  previewUrl?: string;
  removeBranding?: boolean;
  printMode?: string;
  optimize?: boolean;
  createdAt: number;
};
